import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate } from 'react-router';
import { z } from 'zod';
import { accountLinkTokenSchema } from '@bekebi/contracts/api';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@bekebi/domain';
import { ApiError } from '../api/client';
import { AuthCard, authLinkClass } from '../components/auth-card';
import { Button } from '../components/button';
import { LoadingState } from '../components/states';
import { useAccountLink, useCompleteAccountLink } from '../features/account/api';
import { formatDateTime } from '../features/services/format';
import type { LoginLocationState } from '../session/auth-actions';

/**
 * Set your password (/account/set-password#<token>): where invitation and password reset links
 * open (DECISIONS.md A-AC1). The token is in the URL fragment, which browsers never send to a
 * server; it is read once into memory and removed from the address bar and history straight away.
 * Opening the page changes nothing: the link is used only when a password is submitted. The server
 * decides whether the link still works; a closed link shows one message that names nobody.
 */

const formSchema = z
  .object({
    password: z
      .string()
      .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
      .max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters.`),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The passwords do not match.',
  });
type Form = z.infer<typeof formSchema>;

const DONE_STATE: LoginLocationState = { reason: 'password_set' };

/** The token from the fragment, if it has the right shape; otherwise null. */
function readToken(): string | null {
  const raw = window.location.hash.replace(/^#/, '');
  return accountLinkTokenSchema.safeParse(raw).success ? raw : null;
}

export function SetPasswordPage() {
  const [token] = useState(readToken);
  useEffect(() => {
    // Keep the token out of the address bar, the history and anything copied from them.
    if (window.location.hash) {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
  }, []);
  const link = useAccountLink(token);
  const complete = useCompleteAccountLink();
  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<Form>({ resolver: zodResolver(formSchema) });

  if (complete.isSuccess) return <Navigate to="/login" replace state={DONE_STATE} />;
  const closed =
    token === null ||
    (link.error instanceof ApiError && link.error.code === 'link_closed') ||
    (complete.error instanceof ApiError && complete.error.code === 'link_closed');
  if (closed) return <ClosedLink />;
  if (link.isPending) return <LoadingState label="Checking your link…" />;
  if (link.isError) {
    return (
      <AuthCard title="Set your password">
        <p role="alert" className="mt-6 text-sm text-danger">
          {link.error.message}
        </p>
        <Button variant="secondary" className="mt-4 w-full" onClick={() => void link.refetch()}>
          Try again
        </Button>
      </AuthCard>
    );
  }

  const invitation = link.data.purpose === 'invitation';
  const onSubmit = handleSubmit((values) =>
    complete.mutate(
      { token: token!, password: values.password },
      {
        onSettled: () => {
          resetField('password');
          resetField('confirmPassword');
        },
      },
    ),
  );

  const field = (name: keyof Form, label: string, hint?: string) => {
    const error = errors[name];
    const describedBy = [hint ? `${name}-hint` : null, error ? `${name}-error` : null]
      .filter(Boolean)
      .join(' ');
    return (
      <div>
        <label htmlFor={name} className="block text-sm font-medium text-fg">
          {label}
        </label>
        <input
          id={name}
          type="password"
          autoComplete="new-password"
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={describedBy || undefined}
          className="mt-1.5 block h-11 w-full border px-3.5 text-sm"
          {...register(name)}
        />
        {hint ? (
          <p id={`${name}-hint`} className="mt-1.5 text-xs text-muted">
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={`${name}-error`} className="mt-1.5 text-sm text-danger">
            {error.message}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <AuthCard title={invitation ? 'Set up your account' : 'Choose a new password'}>
      <p className="mt-2 text-center text-sm text-muted">
        {invitation
          ? 'Choose the password you will use to sign in to the Bekebi Portal.'
          : 'Choose a new password for your Bekebi Portal account.'}
      </p>
      <dl className="mt-6 rounded-lg border border-line bg-raised px-4 py-3 text-sm">
        <dt className="text-xs text-muted">Account</dt>
        <dd className="mt-0.5 break-all font-medium text-fg">{link.data.email}</dd>
        <dt className="mt-2 text-xs text-muted">This link works until</dt>
        <dd className="mt-0.5 text-fg-2">{formatDateTime(link.data.expiresAt)}</dd>
      </dl>
      {complete.isError ? (
        <p
          role="alert"
          className="mt-6 rounded-lg border border-danger-line bg-danger-soft px-4 py-3 text-sm text-fg"
        >
          {complete.error instanceof ApiError
            ? complete.error.message
            : 'Your password could not be saved. Try again in a moment.'}
        </p>
      ) : null}
      <form onSubmit={onSubmit} noValidate className="mt-6 space-y-5" aria-label="Set password">
        {/* For password managers: the account this password belongs to. */}
        <input type="email" autoComplete="username" value={link.data.email} readOnly hidden />
        {field(
          'password',
          'New password',
          `At least ${PASSWORD_MIN_LENGTH} characters. Use one you do not use anywhere else.`,
        )}
        {field('confirmPassword', 'Confirm new password')}
        <Button type="submit" className="h-11 w-full" disabled={complete.isPending}>
          {complete.isPending ? 'Saving…' : 'Save password'}
        </Button>
      </form>
      <p className="mt-7 border-t border-line pt-5 text-center text-xs text-muted">
        This link works once. After you save, sign in with your email address and new password.
      </p>
    </AuthCard>
  );
}

function ClosedLink() {
  return (
    <AuthCard title="This link no longer works">
      <p role="alert" className="mt-4 text-center text-sm text-fg-2">
        It has already been used, has expired, or was replaced by a newer link.
      </p>
      <ul className="mt-6 list-disc space-y-2 pl-5 text-sm text-fg-2">
        <li>
          If you have already set your password,{' '}
          <Link to="/login" className={authLinkClass}>
            sign in
          </Link>
          .
        </li>
        <li>
          If you have forgotten it,{' '}
          <Link to="/forgot-password" className={authLinkClass}>
            reset your password
          </Link>
          .
        </li>
        <li>
          If you were invited and the link expired, ask the portal administrator to resend it.
        </li>
      </ul>
    </AuthCard>
  );
}
